import { useState } from "react";

import { Button } from "../Button";
import { Image } from "../Image";
import { useChatClass, useTheme } from "../../theme";
import { Container, Content, Description, Header, Link } from "./styled";
import { CardProps } from "./types";
import { isValidHttpUrl } from "./utils";

export type { CardActionProps, CardProps } from "./types";

interface CardComponent extends React.FC<CardProps> {
  Container: typeof Container;
}

/** Do NOT display more than X actions below the description */
const MAX_ACTIONS = 2;

/** A titled card with content and optional controls. */
const Card: CardComponent = ({
  title,
  description,
  image,
  actions = [],
}: CardProps) => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [moreButtons, setMoreButtons] = useState({
    showAll: false,
    label: "Show more",
    hasMore: actions.length > MAX_ACTIONS,
  });
  const theme = useTheme();
  const chatClass = useChatClass();

  const buttonsToDisplay = moreButtons.showAll
    ? actions
    : actions.slice(0, MAX_ACTIONS);

  const isLink = isValidHttpUrl(description);

  function handleShowMore() {
    setMoreButtons((prev) => ({
      showAll: !prev.showAll,
      label: prev.showAll ? "Show more" : "Show less",
      hasMore: prev.hasMore,
    }));
  }

  return (
    <Container className={chatClass("card")}>
      {Boolean(image) && <Image.Default image={image!} />}
      <Content>
        {Boolean(title) && <Header>{title}</Header>}
        {Boolean(description) &&
          (isLink ? (
            <Link rel="noopener" href={description} target="_blank">
              {description}
            </Link>
          ) : (
            <Description
              onClick={() => setShowFullDescription(!showFullDescription)}
              $lineClamp={showFullDescription ? undefined : "8"}
            >
              {description}
            </Description>
          ))}
        {buttonsToDisplay.map(({ name, url, onClick }) => {
          if (url) {
            return (
              <Link
                onClick={onClick}
                key={url}
                rel="noopener"
                href={url}
                target="_blank"
                style={{ textDecoration: "none" }}
              >
                <Button
                  variant="info"
                  style={{ color: theme.colors.primaryDark }}
                >
                  {name}
                </Button>
              </Link>
            );
          } else {
            // if no URL is provided, use the onClick function
            return (
              <Button
                variant="info"
                style={{ color: theme.colors.primaryDark }}
                onClick={onClick}
                key={name}
              >
                {name}
              </Button>
            );
          }
        })}
        {moreButtons.hasMore && (
          <Button
            variant="info"
            onClick={handleShowMore}
            style={{ color: theme.colors.primaryDark, fontWeight: "normal" }}
          >
            {moreButtons.label}
          </Button>
        )}
      </Content>
    </Container>
  );
};

Card.Container = Container;

export { Card };
